<template>
  <div class="demo-page-container container-fluid">
    <h2>IoT Proof of Concept Demonstration</h2>
    
    <div class="d-flex align-items-center flex-wrap gap-2 w-100 my-3">
      
      <!-- 1. Left Side: Device Selection Dropdown -->
      <div class="dropdown">
        <button class="btn btn-secondary dropdown-toggle" type="button" id="deviceSelector"
            data-bs-toggle="dropdown" aria-expanded="false">
          {{ selectedDevice ? selectedDevice.imsi : 'Select Device' }}
        </button>
        <ul class="dropdown-menu" aria-labelledby="deviceSelector">
          <li v-if="selectedDevice">
            <a class="dropdown-item text-danger border-bottom" href="#" @click.prevent="selectDevice(null)">
              Clear Selection
            </a>
          </li>
          <li v-for="device in devices" :key="device.imsi">
            <a class="dropdown-item" href="#" @click.prevent="selectDevice(device)">
              {{ device.description ?? device.id }}
              <span class="small text-muted ms-1">(IMSI: {{ device.imsi }})</span>
              <span class="small text-success ms-1">{{ devicesWithData.includes(device.id) ? '*' : '' }}</span>
            </a>
          </li>
        </ul>
      </div>


      <!-- 2. Right-Aligned Group Container: ms-auto shoves this entire group to the far right -->
      <div class="d-flex align-items-center gap-3 ms-auto">
        
        <!-- Polling Selector Layout Container -->
        <div class="d-flex align-items-center gap-1" style="width: 140px;">
          <label 
            for="inputPolling" 
            class="form-label small fw-bold me-1" 
            style="font-size: 0.75rem; white-space: nowrap;" 
          >
            Refresh [s]
          </label>                
          <input 
            type="number" 
            class="form-control form-control-sm text-end" 
            id="inputPolling" 
            min="0"
            max="3600" 
            v-model.number.lazy="refreshInterval"
            @change="changePolling(parseInt($event.target.value, 10))"
            v-tooltip title="Query the database every N seconds (0 = disabled)" 
          />
        </div>

        <!-- 3. Admin Actions: Sits immediately to the right of Polling when visible -->
        <div v-if="auth.isAdmin" class="d-flex align-items-center gap-2">
          
          <ToggleLockedButton 
            v-model="includeLocked" 
            @toggle="toggleLockedFilter"
          />

          <SyncControl 
            :endpoint="historyUrl"  
            @syncComplete="syncComplete" 
          />

        </div>

      </div>

    </div>

    <ul class="nav nav-tabs" id="demoTab" role="tablist">
      <li class="nav-item">
        <button class="nav-link active" 
          id="loc-tab" 
          data-bs-toggle="tab" 
          data-bs-target="#location" 
          type="button" 
          role="tab"
        >
          Location
        </button>
      </li>
      <li class="nav-item">
        <button class="nav-link" 
          id="chat-tab" 
          data-bs-toggle="tab" 
          data-bs-target="#chat" 
          type="button" 
          role="tab"
        >
          Text/Chat
        </button>
      </li>
      <li class="nav-item">
        <button class="nav-link" 
          id="telemetry-tab" 
          data-bs-toggle="tab" 
          data-bs-target="#telemetry" 
          type="button" 
          role="tab"
        >
          Telemetry
        </button>
      </li>
      <li class="nav-item">
        <button class="nav-link" 
          id="ecall-tab" 
          data-bs-toggle="tab" 
          data-bs-target="#ecall" 
          type="button" 
          role="tab"
        >
          eCall / PSAP
        </button>
      </li>
      <li class="nav-item">
        <button class="nav-link" 
          id="history-tab" 
          data-bs-toggle="tab" 
          data-bs-target="#history" 
          type="button" 
          role="tab" 
          @click="getHistory"
        >
          History
        </button>
      </li>
    </ul>

    <div class="tab-content border-start border-end border-bottom p-3" id="demoTabContent">
      
      <div class="tab-pane fade show active" id="location" role="tabpanel">
        <div class="map-container" style="height: 500px;">
          <LocationTrackMap :locations="locationData" />
        </div>
      </div>
      
      <div class="tab-pane fade" id="telemetry" role="tabpanel">
        <div class="chart-wrapper">
          <SignalChart v-if="isTelemetryLoaded && isTelemetryActive"
            :measType="measType" 
            :units="measUnit" 
            :lineLabel="selectedDevice && (selectedDevice.description || selectedDevice.id)" 
            :measurements="measurements" 
          />
        </div>
      </div>
      
      <div class="tab-pane fade" id="chat" role="tabpanel">
        <ChatWindow 
          :selectedDevice="selectedDevice && selectedDevice.id" 
          :messages="textData" 
          :availableDevices="devicesWithText" 
          @sentText="sentMessage" 
        />
      </div>
      
      <div class="tab-pane fade" id="ecall" role="tabpanel">
        <PsapSimulator 
          :selectedDevice="selectedDevice && selectedDevice.id" 
          :ecallMessages="ecallData" 
          @sentEcall="sentMessage" 
        />
      </div>
      
      <div class="tab-pane fade" id="history" role="tabpanel">
        <div>
          <DataForm v-if="modalHistory" 
              :tag="`Metadata`" :msg="`View Metadata`" :schema="entitySchema"
              :schemaTypes="schemaTypes" :parentObj="historyDetailRow" 
              :enforceFocus="false"
              @formClosed="closedModal" 
          />
        </div>
        <DataTable 
          ref="historyTable"
          modelName="decodedMessage"
          :options="tableOptions"
          :ajaxUrl="historyUrl"
          :queryParams="historyQueryParams"
          :orderBy="orderBy"
          :columns="historyColumns" 
        >
          <template #details="row">
            <TableActions mode="details" :rowData="row.rowData" @detail="detailHistory" />
          </template>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, reactive } from 'vue';
import { Tab as BootstrapTab } from 'bootstrap';
import { isPlainObject } from 'lodash-es';
import { useAuthStore } from '@/stores/authStore';
import { useSchemaStore } from '@/stores/schemaStore';
import { ApiEndpoint } from '@/utils/config';
import { apiFetch } from '@/utils/api';
import { getCommonColumns, renderExpandableJson, renderTime } from '@/utils/table';
import { getDemoLocations } from '@/demo/location';
import { getDemoTelemetry } from '@/demo/telemetry';
import { getDemoTextMessages } from '@/demo/text';
import { getDemoEcallMessages } from '@/demo/ecall';

// Components
import ToggleLockedButton from '@/components/ToggleLockedButton.vue';
import SyncControl from '@/components/SyncComponent.vue';

import DataTable from '@/components/TableComponent.vue';
import TableActions from '@/components/TableActions.vue';
import DataForm from '@/components/DataFormModal.vue';
import LocationTrackMap from "@/components/DemoLocationComponent.vue";
import ChatWindow from '@/components/DemoChatComponent.vue';
import SignalChart from '@/components/LineChartComponent.vue';
import PsapSimulator from '@/components/DemoPsapComponent.vue';

const auth = useAuthStore();
const schema = useSchemaStore();
const entitySchema = ref([]);  // entity schemas are an array of objects
const schemaTypes = reactive({});

// --- State ---
const devices = ref([]);
const selectedDevice = ref(null);

// const isHistoryTabActive = ref(false);
const history = ref([]);
const historyTable = ref();
const modalHistory = ref(false);
const historyDetailRow = reactive({});

const includeLocked = ref(false);
const refreshInterval = ref(0);
let refreshTimer = null;

const devicesWithText = ref([]);
const devicesWithLoc = ref([]);
const devicesWithTelm = ref([]);
const devicesWithEcall = ref([]);

// --- Config ---
const historyColumns = computed(() => [
  { title: 'Event Time', data: 'timestamp', width: '15%', render: renderTime },
  { title: 'Device', data: 'deviceId', width: '15%', defaultContent: '', class: 'td-truncate' },
  { title: 'Event Name', data: 'name', width: '15%' },
  { title: 'Direction', data: 'direction' },
  { title: 'Event Data', data: 'payload', render: renderExpandableJson },
  ...getCommonColumns(auth.isAdmin, true),
]);

const orderBy = { timestamp: 'desc' };
const tableOptions = {
  fixedColumns: { left: 1, right: 1 },
};

const devicesWithData = computed(() => {
  return [
    ...new Set([
      ...devicesWithLoc.value,
      ...devicesWithTelm.value,
      ...devicesWithText.value,
      ...devicesWithEcall.value,
    ]),
  ]
});

const filterDataByDevice = (deviceId, data) => {
  if (deviceId in data) return data[deviceId];
  return [];
};

const locationData = computed(() => {
  const rows = historyTable.value?.visibleRows || [];
  if (!rows.length) return selectedDevice.value ? [] : {};
  const { locations, total } = getDemoLocations(rows);
  devicesWithLoc.value = Object.keys(locations);
  console.debug(`Processing ${total} locations`);
  if (selectedDevice.value) {
    return filterDataByDevice(selectedDevice.value.id, locations);
  }
  return locations;
});

const telemetryData = computed(() => {
  const rows = historyTable.value?.visibleRows || [];
  if (!rows.length) return selectedDevice.value ? [] : {};
  const { telemetry, total } = getDemoTelemetry(rows);
  console.debug(`Processing ${total} telemetry points`);
  devicesWithTelm.value = Object.keys(telemetry);
  if (!selectedDevice.value) return telemetry;
  return filterDataByDevice(selectedDevice.value.id, telemetry);
});

const isTelemetryActive = ref(false);

const isTelemetryLoaded = computed(() => {
  return (
    (Array.isArray(telemetryData.value) && telemetryData.value.length) ||
    (isPlainObject(telemetryData.value) && 
    Object.keys(telemetryData.value).length)
  );
});

const measType = ref('sinr');
const measUnit = ref('dB');
const measurements = computed(() => {
  if (!telemetryData.value) return selectedDevice.value ? [] : {};
  // Multi-device
  if (!selectedDevice.value) {
    const meas = {};
    for (const deviceId in telemetryData.value) {
      meas[deviceId] = telemetryData.value[deviceId][measType.value] || [];
    }
    return meas;
  }
  return telemetryData.value[measType.value] || [];
});

const textData = computed(() => {
  const rows = historyTable.value?.visibleRows || [];
  if (!rows.length) return [];
  const { textMessages, total } = getDemoTextMessages(rows);
  console.debug(`Processing ${total} text messages`);
  devicesWithText.value = Object.keys(textMessages);
  if (!selectedDevice.value) return [];
  return filterDataByDevice(selectedDevice.value.id, textMessages);
});

const ecallData = computed(() => {
  const rows = historyTable.value?.visibleRows || [];
  if (!rows.length) return [];
  const { ecallMessages, total } = getDemoEcallMessages(rows);
  console.debug(`Processing ${total} e-call messages`);
  devicesWithEcall.value = Object.keys(ecallMessages);
  if (!selectedDevice.value) return [];
  return filterDataByDevice(selectedDevice.value.id, ecallMessages);
});

// --- Methods ---
const getDevices = async () => {
  try {
    const params = { codecId: 'notNull' };
    const { items: filtered } = await apiFetch(`${ApiEndpoint.DEVICES}`, { params });
    devices.value = (filtered || []).map(device => ({
      ...device,
      description: device.description?.trim() ? device.description : null
    }));
    console.debug(`Retrieved ${devices.value.length} device(s) with codec`);
  } catch (err) {
    console.error(`Error getting Devices:`, err.message);
  }
};

const toggleLockedFilter = async () => {
  // includeLocked.value = !includeLocked.value; // this is done by the component
  // Wait one tick loop for the computed queryParams property to update values
  await nextTick();
  if (historyTable.value) {
    // Pass true to automatically reset pagination back to page 1
    await historyTable.value.reloadTable(true);
  }
};

const selectDevice = async (device) => {
  selectedDevice.value = device;
  // startPolling();
  await getHistory(true);
};

// const mergeMessages = (existing, incoming) => {
//   return [
//     ...existing,
//     ...incoming.filter(im => !existing.some(em => em.messageId === im.messageId))
//   ];
// };

const historyUrl = ApiEndpoint.DEMO;
const historyQueryParams = computed(() => {
  return {
    deviceId: selectedDevice.value?.id || undefined,
    includeLocked: (includeLocked.value === true) || undefined,
  }
});

const getHistory = async () => {
  // isHistoryTabActive.value = true;
  // Wait exactly one tick loop for the table element to mount into the DOM tree structure cleanly
  await nextTick();
  // Force DataTables to read parent boundaries and execute its first clean server network fetch
  if (historyTable.value) {
    await historyTable.value.reloadTable();
  }
  return;
};

const detailHistory = (row) => {
  Object.assign(historyDetailRow, row);
  modalHistory.value = true;
};

const closedModal = async () => {
  // 1. Instantly strip keyboard focus off the closing modal element
  if (document.activeElement && typeof document.activeElement.blur === 'function') {
    document.activeElement.blur();
  }
  // 2. Move focus to a safe, persistent target in the main view
  const deviceButton = document.getElementById('deviceSelector');
  if (deviceButton) {
    deviceButton.focus();
  }
  // 3. Clear state flag on the next tick loop once focus is clear
  await nextTick();
  modalHistory.value = false;
  if (historyTable.value) {
    await historyTable.value.reloadTable();
  }
};

const syncComplete = async() => {
  await getHistory(true);
};

const changePolling = (pollingInterval = 0) => {
  if (pollingInterval < 0) pollingInterval = 0;
  if (pollingInterval > 3600) pollingInterval = 3600;
  if (refreshTimer) clearInterval(refreshTimer);
  if (pollingInterval > 0) {
    console.debug(`Polling every ${pollingInterval} seconds...`);
    refreshTimer = setInterval(getHistory, pollingInterval * 1000);
  } else {
    console.debug(`Stopping polling`);
    refreshTimer = null;
  }
  refreshInterval.value = pollingInterval;
};

const sentMessage = async (messageMeta) => {
  await getHistory();
};

onMounted(async () => {
  entitySchema.value = schema.getFieldMeta('decodedMessage');
  schemaTypes.value = schema.getModelEnums('decodedMessage');
  await getDevices();
  
  const savedTabTarget = localStorage.getItem('demoActiveTabTarget');

  const tabElements = document.querySelectorAll('button[data-bs-toggle="tab"]');
  tabElements.forEach(tabEl => {
    tabEl.addEventListener('shown.bs.tab', async (event) => {
      const targetId = event.target.getAttribute('data-bs-target');
      
      // Save the active choice to local memory for the next reload cycle
      localStorage.setItem('demoActiveTabTarget', targetId);

      if (targetId === '#telemetry') {
        await nextTick();
        isTelemetryActive.value = true;
      } else {
        isTelemetryActive.value = false;
      }

      await getHistory();

    }, { passive: true });
  });

  if (savedTabTarget) {
    // Locate the matching tab button node by its data-bs-target attribute string
    const tabTriggerEl = document.querySelector(`[data-bs-target="${savedTabTarget}"]`);
    if (tabTriggerEl) {
      // Force Bootstrap to activate the saved view panel instantly
      const tabInstance = new BootstrapTab(tabTriggerEl);
      tabInstance.show();
    }
  }

});

onBeforeUnmount(() => {
  if (refreshInterval.value) clearInterval(refreshInterval.value);
  localStorage.removeItem('demoActiveTabTarget');
});
</script>

<style scoped>

.demo-page-container {
  display: flex;
  flex-direction: column;
  /* calc(100vh - Navbar_Height - SubHeader_Height) */
  /* This prevents the page body from scrolling and locks it to the open DevTools line */
  height: calc(100vh - 100px); 
  overflow: hidden;
}

/* Force the active tab panel to stretch and fill the remaining bottom space */
.dynamic-tab-content-wrapper, 
:deep(.tab-content), 
:deep(.tab-pane.active) {
  background-color: var(--bs-body-bg);
  flex-grow: 1;
  height: 100%;
  overflow: hidden;
}

.map-container {
  width: 100%;
  border: 1px solid #ddd;
}

/* Force your chart wrapper and its canvas container to stretch vertically */
.chart-wrapper {
  flex-grow: 1;
  width: 100%;
  height: 100%;
  position: relative; /* Crucial if using Chart.js resize tracking */
  margin-bottom: 1rem;
}

#history,
:deep(.tab-pane.active)#history-pane { /* or whatever your history pane selector is */
  overflow-y: auto !important; /* Force overrides the 'hidden' rule above */
  height: 100%;
  display: block; /* Ensures standard block flow behavior for table scrolling */
}

#refactored-chunk {
  /* Force cells to track text structures safely without overflowing */
  :deep(.table) {
    width: 100% !important;
    margin-bottom: 0 !important;
  }

  :deep(td), :deep(th) {
    vertical-align: middle !important;
  }

  /* Format your custom Expandable JSON details tree aesthetic layout */
  :deep(details.json-cell-drawer) {
    display: block;
    background-color: transparent;
  }

  :deep(details.json-cell-drawer summary) {
    list-style: none;
    color: #0dcaf0 !important; /* Standard clean Bootstrap info cyan color marker */
    cursor: pointer;
    user-select: none;
  }

  :deep(details.json-cell-drawer summary::-webkit-details-marker) {
    display: none; /* Hide standard browser bullet points */
  }

  :deep(details.json-cell-drawer pre) {
    background-color: #111418 !important; /* Lean, dark background layer */
    border: 1px solid #495057 !important;
    color: #66d9ef !important;
    text-align: left;
    font-size: 0.75rem;
    max-height: 250px;
    overflow-y: auto;
    white-space: pre-wrap;
  }
}

</style>
